import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import { ChildDetailsUpdateDto } from '@/models/in/ManagementChildDtoIn';
import { usePutAuthApi } from '@/hooks/UseApiCalls';
import { isValidIsraeliID } from '@/helpers/israeliIdValidator';
import { formatISODate, isBirthDateInAllowedRange, MIN_BIRTH_DATE_INPUT, MIN_PLAUSIBLE_YEAR } from '@/helpers/dateHelper';
import Button from '@/components/ui/Button';
import styles from './EditChildDialog.module.css';

/**
 * The child details this dialog edits. Kept minimal so both a children management table row and a
 * supported's children list can hand one over.
 */
export interface EditableChildDetails {
  childId: number;
  firstName: string;
  lastName: string;
  israeliId: string;
  birthDate?: string | Date | null;
}

interface EditChildDialogProps {
  open: boolean;
  onClose: () => void;
  child: EditableChildDetails;
  onSuccess: () => void;
}

// Which field the current error belongs to, so it can be highlighted
type ErrorState = { field: 'israeliId' | 'birthDate' | 'general'; message: string };

const EditChildDialog = ({
  open,
  onClose,
  child,
  onSuccess,
}: EditChildDialogProps) => {
  const { t } = useTranslation();
  const [israeliId, setIsraeliId] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [error, setError] = useState<ErrorState | null>(null);

  const childName = `${child.firstName} ${child.lastName}`;

  const { mutate: updateChildDetails, isPending: isSubmitting } = usePutAuthApi<void, object, ChildDetailsUpdateDto>(
    `management/children/${child.childId}`
  );

  // Initialize form when dialog opens
  useEffect(() => {
    if (open) {
      setIsraeliId(child.israeliId || '');
      // Format date for input (YYYY-MM-DD) using local timezone
      if (child.birthDate) {
        setBirthDate(formatISODate(new Date(child.birthDate)));
      } else {
        setBirthDate('');
      }
      setError(null);
    }
  }, [open, child]);

  const handleSubmit = () => {
    // Validate Israeli ID
    if (!israeliId.trim()) {
      setError({ field: 'israeliId', message: t('childrenManagement.editChildDialog.israeliIdRequired') });
      return;
    }

    if (!isValidIsraeliID(israeliId)) {
      setError({ field: 'israeliId', message: t('childrenManagement.editChildDialog.invalidIsraeliId') });
      return;
    }

    // Birth date is a required detail for a child - it cannot be cleared
    if (!birthDate) {
      setError({ field: 'birthDate', message: t('childrenManagement.editChildDialog.birthDateRequired') });
      return;
    }

    // ...and it has to be a date someone could actually have been born on (SPR-1179)
    if (!isBirthDateInAllowedRange(birthDate)) {
      setError({ field: 'birthDate', message: t('birthDateTooEarly', { year: MIN_PLAUSIBLE_YEAR }) });
      return;
    }

    setError(null);

    const body: ChildDetailsUpdateDto = {
      israeliId: israeliId.trim().padStart(9, '0'),
      birthDate,
    };

    updateChildDetails(
      { body },
      {
        onSuccess: () => {
          onSuccess();
          handleClose();
        },
        onError: (err) => {
          setError({
            field: 'general',
            message: t('childrenManagement.editChildDialog.errorUpdating') + ': ' + err.message,
          });
        },
      }
    );
  };

  const handleClose = () => {
    setIsraeliId('');
    setBirthDate('');
    setError(null);
    onClose();
  };

  return (
    <MainDialogLayout
      open={open}
      onOpenChange={(isOpen: boolean) => {
        if (!isOpen) {
          handleClose();
        }
      }}
      title={t('childrenManagement.editChildDialog.title')}
    >
      <div className={styles.content}>
        <p className={styles.childName}>{childName}</p>

        <div className={styles.inputSection}>
          <label htmlFor="israeliId" className={styles.inputLabel}>
            {t('childrenManagement.editChildDialog.israeliId')}
          </label>
          <input
            id="israeliId"
            type="text"
            className={`${styles.input} ${error?.field === 'israeliId' ? styles.hasError : ''}`}
            value={israeliId}
            onChange={(e) => {
              setIsraeliId(e.target.value);
              if (error) setError(null);
            }}
            maxLength={9}
            dir="ltr"
          />
        </div>

        <div className={styles.inputSection}>
          <label htmlFor="birthDate" className={styles.inputLabel}>
            {t('childrenManagement.editChildDialog.birthDate')}
          </label>
          <input
            id="birthDate"
            type="date"
            min={MIN_BIRTH_DATE_INPUT}
            className={`${styles.input} ${error?.field === 'birthDate' ? styles.hasError : ''}`}
            value={birthDate}
            onChange={(e) => {
              setBirthDate(e.target.value);
              if (error) setError(null);
            }}
            dir="ltr"
          />
        </div>

        {error && (
          <p className={styles.error}>{error.message}</p>
        )}

        <div className={styles.actions}>
          <Button
            variant="white"
            className={styles.secondaryButton}
            onClick={handleClose}
            disabled={isSubmitting}
          >
            {t('close')}
          </Button>
          <Button
            variant="primary"
            className={styles.actionButton}
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? t('saving') : t('save')}
          </Button>
        </div>
      </div>
    </MainDialogLayout>
  );
};

export default EditChildDialog;

